import React, { useEffect, useState, useCallback } from "react";
import { staffQueue, facilityAdmin } from "../../api";
import {
  PageHeader, Btn, EmptyState, LoadingSpinner, ConfirmModal, selectStyle,
} from "../../components/ui";
import Modal from "../../components/ui/Modal";
import toast from "react-hot-toast";

// Facility-wide escape hatch: every other queue action (call, transfer,
// complete) is scoped to the logged-in staff member's own assigned
// station — correct day to day, but it leaves a real gap when a ticket
// is sitting at a station whose staff member isn't available (out sick,
// forgot to log in, etc.). Nobody else has any way to move that patient
// along short of logging in as that staff member, which isn't a real
// answer. This page lets a facility admin see every active ticket
// regardless of station and either move it to a different station or
// release it (mark abandoned) directly.
export default function QueueOverridePage() {
  const [tickets,  setTickets]  = useState([]);
  const [stations, setStations] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [moveModal, setMoveModal] = useState(null);
  const [releaseConfirm, setReleaseConfirm] = useState(null);
  const [saving,   setSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const tr = await staffQueue.allActiveTickets();
      setTickets(tr.data || []);
    } catch {
      toast.error("Failed to load active tickets.");
    }
    try {
      const sr = await facilityAdmin.listStations();
      setStations(sr.data?.results || sr.data || []);
    } catch {
      toast.error("Failed to load stations — the Move action won't have any options until this loads.");
      setStations([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleMove = async (targetStationId) => {
    if (!moveModal) return;
    setSaving(true);
    try {
      await staffQueue.adminMoveTicket(moveModal.id, targetStationId);
      toast.success(`${moveModal.ticket_number} moved.`);
      setMoveModal(null); load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to move ticket.");
    } finally { setSaving(false); }
  };

  const handleRelease = async () => {
    if (!releaseConfirm) return;
    setSaving(true);
    try {
      await staffQueue.adminReleaseTicket(releaseConfirm.id);
      toast.success(`${releaseConfirm.ticket_number} released.`);
      setReleaseConfirm(null); load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to release ticket.");
    } finally { setSaving(false); }
  };

  // Group by current station for a clearer picture of where things are stuck
  const byStation = {};
  tickets.forEach(t => {
    const key = t.current_station_name || "Unassigned";
    if (!byStation[key]) byStation[key] = [];
    byStation[key].push(t);
  });

  return (
    <div style={styles.root}>
      <PageHeader
        title="Queue Override"
        subtitle="Every active ticket across all stations. Use this when a ticket is stuck because that station's staff member isn't available — move it elsewhere or release it, no login needed."
      />

      {loading ? <LoadingSpinner /> : tickets.length === 0 ? (
        <EmptyState icon="✅" title="No active tickets" subtitle="Nothing waiting or being served right now." />
      ) : (
        <div style={styles.col}>
          {Object.entries(byStation).map(([stationName, list]) => (
            <div key={stationName} style={styles.card}>
              <div style={styles.cardTitle}>{stationName} <span style={styles.count}>{list.length}</span></div>
              <div style={styles.list}>
                {list.map(t => (
                  <div key={t.id} style={styles.row}>
                    <div style={styles.rowMain}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={styles.ticketNum}>{t.ticket_number}</span>
                        {t.priority !== "normal" && (
                          <span style={{ fontSize: "12px", color: t.priority === "emergency" ? "#EF4444" : "#EAB308" }}>
                            {t.priority === "emergency" ? "🚨" : "⚡"}
                          </span>
                        )}
                        <span style={{
                          fontSize: "10px", fontWeight: "700", padding: "2px 7px", borderRadius: "5px",
                          textTransform: "uppercase",
                          background: t.status === "serving" ? "rgba(37,99,235,0.1)" : "rgba(234,179,8,0.1)",
                          color:      t.status === "serving" ? "#2563EB" : "#EAB308",
                        }}>
                          {t.status}
                        </span>
                      </div>
                      <div style={styles.patientName}>{t.patient_name}</div>
                      <div style={styles.meta}>
                        Waiting {Math.round(t.total_wait_minutes ?? 0)} min
                        {t.current_staff_name && ` · Staff: ${t.current_staff_name}`}
                      </div>
                    </div>
                    <div style={styles.rowActions}>
                      <Btn size="sm" variant="ghost" onClick={() => setMoveModal(t)}>Move to Station</Btn>
                      <Btn size="sm" variant="danger" onClick={() => setReleaseConfirm(t)}>Release</Btn>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <MoveModal
        open={!!moveModal}
        onClose={() => setMoveModal(null)}
        ticket={moveModal}
        stations={stations}
        onMove={handleMove}
        saving={saving}
      />
      <ConfirmModal
        open={!!releaseConfirm}
        onClose={() => setReleaseConfirm(null)}
        onConfirm={handleRelease}
        loading={saving}
        title="Release this ticket?"
        message={`"${releaseConfirm?.ticket_number} — ${releaseConfirm?.patient_name}" will be marked abandoned and removed from the active queue. Use this when there's no station to move them to.`}
        confirmLabel="Release"
        variant="danger"
      />
    </div>
  );
}

function MoveModal({ open, onClose, ticket, stations, onMove, saving }) {
  const [target, setTarget] = useState("");
  useEffect(() => { if (open) setTarget(""); }, [open, ticket]);
  const otherStations = stations.filter(s => s.name !== ticket?.current_station_name);

  return (
    <Modal open={open} onClose={onClose} title={`Move ${ticket?.ticket_number || ""}`} width="420px">
      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        <div style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
          Currently at <strong style={{ color: "var(--text-primary)" }}>{ticket?.current_station_name || "no station (unassigned)"}</strong>.
          The patient rejoins the queue at the new station as waiting.
        </div>
        {otherStations.length === 0 ? (
          <div style={{ fontSize: "13px", color: "#EF4444", padding: "10px 0" }}>
            No other stations found. Check that more than one station exists under
            Zones &amp; Stations, then close and reopen this dialog.
          </div>
        ) : (
          <select style={selectStyle} value={target} onChange={e => setTarget(e.target.value)}>
            <option value="">— Select a station —</option>
            {otherStations.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", paddingTop: "8px", borderTop: "1px solid var(--surface-border)" }}>
          <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn onClick={() => onMove(target)} disabled={saving || !target}>{saving ? "Moving…" : "Move Ticket"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

const styles = {
  root: { padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "900px" },
  col:  { display: "flex", flexDirection: "column", gap: "16px" },
  card: {
    background: "var(--surface-bg)", border: "1px solid var(--surface-border)",
    borderRadius: "14px", padding: "18px",
  },
  cardTitle: {
    fontSize: "13px", fontWeight: "700", color: "var(--text-primary)",
    display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px",
  },
  count: {
    fontSize: "11px", fontWeight: "700", color: "#60A5FA",
    background: "var(--surface-border)", borderRadius: "10px", padding: "1px 8px",
  },
  list: { display: "flex", flexDirection: "column", gap: "8px" },
  row: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: "16px", padding: "12px 14px", borderRadius: "10px",
    background: "var(--input-bg)", border: "1px solid var(--surface-border)",
  },
  rowMain: { flex: 1, minWidth: 0 },
  ticketNum: { fontSize: "15px", fontWeight: "800", color: "var(--text-primary)", fontVariantNumeric: "tabular-nums" },
  patientName: { fontSize: "13px", color: "var(--text-secondary)", marginTop: "2px" },
  meta: { fontSize: "11px", color: "var(--text-muted)", marginTop: "2px" },
  rowActions: { display: "flex", gap: "8px", flexShrink: 0 },
};
